import { Spinner } from 'design-system';

import { useFlowchartImage } from '@/features/factsheet/api/queries';
import { getVersionHistoryField } from '@/features/factsheet/lib/versionHistory';
import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import type { FactsheetDetails } from '@/features/factsheet/types';
import { cn } from '@/lib/utils';

import RelatedCodeList from '../RelatedCodesTab/RelatedCodeList';

interface FlowchartProps {
  flowchartFileId: string;
  className?: string;
}

const Flowchart = ({ flowchartFileId, className }: FlowchartProps) => {
  const { data: imageSrc, isError } = useFlowchartImage(flowchartFileId);

  if (isError) {
    return null;
  }

  return (
    <div className={cn('flex w-full flex-col items-center', className)}>
      {imageSrc ? (
        <img
          alt='flowchart'
          src={imageSrc}
          className='w-[90%]'
        />
      ) : (
        <div className='flex h-[250px] w-full items-center justify-center'>
          <Spinner />
        </div>
      )}
    </div>
  );
};

interface FlowchartTabProps {
  factsheet: FactsheetDetails;
  openNewTab: (code: string) => void;
}

const FlowchartTab = ({ factsheet, openNewTab }: FlowchartTabProps) => {
  const parentsWithFlowcharts = getVersionHistoryField(factsheet, 'parentsWithFlowcharts') ?? [];
  const flowchartIds = getVersionHistoryField(factsheet, 'flowcharts') ?? [];
  const validFlowchartIds = flowchartIds.filter(id => id && id !== 'false');
  const isCompact = useIsFactsheetCompact();

  if (validFlowchartIds.length > 0) {
    return (
      <div>
        {validFlowchartIds.map((flowchartId, index) => (
          <Flowchart
            key={flowchartId}
            className={index > 0 ? 'mt-4' : undefined}
            flowchartFileId={flowchartId}
          />
        ))}
      </div>
    );
  }

  return (
    <div>
      <p className={cn('pb-1 font-bold', isCompact && 'text-sm')}>Flowchart</p>
      <p className={cn('pb-2', isCompact && 'text-sm')}>None.</p>
      <RelatedCodeList
        openNewTab={openNewTab}
        relatedCodes={parentsWithFlowcharts}
        title='Parent Code Flowchart'
      />
    </div>
  );
};

export default FlowchartTab;
